import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Popover,
  Link,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { toast } from 'react-toastify';
import { getSectionContent, updateSectionContent } from '../services/contentService';
import CloudinaryUploadWidget from './CloudinaryUploadWidget';
import SaveIcon from '@mui/icons-material/Save';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import InfoIcon from '@mui/icons-material/Info';

const commonFontFamilies = [
  'Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Courier New', 
  'Lucida Console', 'Helvetica', 'Roboto', 'Open Sans', 'Lato', 'Montserrat'
];

const SECTION_ID = 'navbarSection'; // Ensure this ID is mapped in contentService.js

const defaultNavbarData = {
  logoUrl: '',
  logoMaxHeight: '32px',
  brandName: 'Fuel Solution',
  backgroundColor: '#0057B8',
  linkColor: '#FFFFFF',
  linkHoverColor: '#DDDDDD',
  activeLinkColor: '#BBBBBB',
  brandNameColor: '#FFFFFF',
  brandNameFontSize: 18, 
  brandNameFontFamily: 'Arial',
  brandNameFontWeight: '600',
  navLinkColor: '#FFFFFF', // Assuming nav links use linkColor by default, but can be separate
  navLinkFontSize: 16,
  navLinkFontFamily: 'Arial',
  navLinkFontWeight: '500',
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`header-tabpanel-${index}`}
      aria-labelledby={`header-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const HeaderEditor = () => {
  const [navbarData, setNavbarData] = useState(defaultNavbarData);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [logoPreview, setLogoPreview] = useState('');

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);

  const loadNavbarData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getSectionContent(SECTION_ID);
      let processedData = data ? { ...defaultNavbarData, ...data } : { ...defaultNavbarData };

      // Process font sizes (string 'px' to number)
      const fontSizeFields = ['brandNameFontSize', 'navLinkFontSize'];
      fontSizeFields.forEach(field => {
        if (processedData[field] !== undefined && processedData[field] !== null) {
          if (typeof processedData[field] === 'string') {
            const parsed = parseFloat(processedData[field].replace('px', ''));
            processedData[field] = isNaN(parsed) ? defaultNavbarData[field] : parsed;
          } else if (typeof processedData[field] !== 'number') {
            processedData[field] = defaultNavbarData[field];
          }
        }
      });

      // Ensure default font families and weights if not present
      const fontStyleFields = [
        { keyPrefix: 'brandName', defaultFamily: 'Arial', defaultWeight: '600' },
        { keyPrefix: 'navLink', defaultFamily: 'Arial', defaultWeight: '500' },
      ];

      fontStyleFields.forEach(field => {
        const familyKey = `${field.keyPrefix}FontFamily`;
        const weightKey = `${field.keyPrefix}FontWeight`;
        if (typeof processedData[familyKey] !== 'string' || !processedData[familyKey]) {
          processedData[familyKey] = field.defaultFamily;
        }
        if (typeof processedData[weightKey] !== 'string' || !processedData[weightKey]) {
          processedData[weightKey] = field.defaultWeight;
        }
      });

      setNavbarData(processedData);
      if (processedData.logoUrl) {
        setLogoPreview(processedData.logoUrl);
      }
    } catch (err) {
      console.error('Failed to load navbar data:', err);
      setError(err.message || 'Failed to load navbar data.');
      toast.error('Failed to load navbar data.');
      setNavbarData({ ...defaultNavbarData }); // Reset to defaults on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNavbarData();
  }, [loadNavbarData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value: rawValue, type } = e.target;
    let value = rawValue;
    const isFontSizeField = name && (name.endsWith('FontSize'));

    if (isFontSizeField && type === 'number') {
      if (rawValue === '') {
        value = ''; // Allow temporarily empty for better UX
      } else {
        const parsed = parseFloat(rawValue);
        value = isNaN(parsed) ? rawValue : parsed; // Keep as string if not a valid number, or use parsed number
      }
    }
    setNavbarData(prev => ({ ...prev, [name]: value }));
  };

  const handleColorPickerOpen = (colorField, event) => {
    setActiveColorPicker(colorField);
    setColorPickerAnchorEl(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchorEl(null);
  };

  const handleColorChange = (color) => {
    if (activeColorPicker) {
      setNavbarData(prev => ({ ...prev, [activeColorPicker]: color.hex }));
    }
  };

  const handleLogoUploadSuccess = (secureUrl) => {
    setNavbarData(prev => ({ ...prev, logoUrl: secureUrl }));
    setLogoPreview(secureUrl);
    toast.info('Logo selected. Click "Save Header Settings" to apply.');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSectionContent(SECTION_ID, navbarData);
      toast.success('Header settings updated successfully!');
    } catch (err) {
      console.error('Failed to save navbar data:', err);
      setError(err.message || 'Failed to save navbar data.');
      toast.error(err.message || 'Failed to save navbar data.');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 200px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading header settings: {error}</Alert>
        <Button onClick={loadNavbarData} sx={{ mt: 2 }}>Try Again</Button>
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, m: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Edit Header Settings
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || isLoading}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Header Settings'}
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="header editor tabs">
          <Tab label="Logo & Brand" id="header-tab-0" aria-controls="header-tabpanel-0" />
          <Tab label="Styling" id="header-tab-1" aria-controls="header-tabpanel-1" />
          <Tab label="Navigation Links Info" id="header-tab-2" aria-controls="header-tabpanel-2" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Logo Settings</Typography>
            <CloudinaryUploadWidget
              onUploadSuccess={handleLogoUploadSuccess}
              folder={SECTION_ID}
              buttonText="Upload Logo"
            />
            {logoPreview && (
              <Box sx={{ mt: 2, p: 1, border: '1px dashed grey', display: 'inline-block' }}>
                <Typography variant="caption" display="block" gutterBottom>Current Logo Preview:</Typography>
                <img src={logoPreview} alt="Logo Preview" style={{ maxHeight: '80px', maxWidth: '200px' }} />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Logo Max Height (e.g., '32px', '2.5rem')"
              name="logoMaxHeight"
              value={navbarData.logoMaxHeight || ''}
              onChange={handleInputChange}
              variant="outlined"
              helperText="Controls the maximum height of the logo in the header."
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{mt: 2}}>Brand Name</Typography>
            <TextField
              fullWidth
              label="Header Brand Text"
              name="brandName"
              value={navbarData.brandName || ''}
              onChange={handleInputChange}
              variant="outlined"
              helperText="Text displayed usually next to the logo."
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Header Background Color */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Header Background</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Background Color:</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    onClick={(e) => handleColorPickerOpen('backgroundColor', e)}
                    startIcon={<ColorLensIcon />}
                    fullWidth
                  >
                    {navbarData.backgroundColor}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Brand Name Styling */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Brand Name Styling</Typography>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Text Color:</Typography>
                  <Button
                    variant="outlined"
                    onClick={(e) => handleColorPickerOpen('brandNameColor', e)}
                    startIcon={<ColorLensIcon />}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {navbarData.brandNameColor}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Font Size (px)"
                    type="number"
                    name="brandNameFontSize"
                    value={navbarData.brandNameFontSize || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="Pixel value"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Font Family</InputLabel>
                    <Select
                      name="brandNameFontFamily"
                      value={navbarData.brandNameFontFamily || 'Arial'}
                      onChange={handleInputChange}
                      label="Font Family"
                    >
                      {commonFontFamilies.map(font => (
                        <MenuItem key={font} value={font}>{font}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Font Weight</InputLabel>
                    <Select
                      name="brandNameFontWeight"
                      value={navbarData.brandNameFontWeight || '600'}
                      onChange={handleInputChange}
                      label="Font Weight"
                    >
                      <MenuItem value="100">100 (Thin)</MenuItem>
                      <MenuItem value="200">200 (Extra Light)</MenuItem>
                      <MenuItem value="300">300 (Light)</MenuItem>
                      <MenuItem value="400">400 (Normal)</MenuItem>
                      <MenuItem value="500">500 (Medium)</MenuItem>
                      <MenuItem value="600">600 (Semi Bold)</MenuItem>
                      <MenuItem value="700">700 (Bold)</MenuItem>
                      <MenuItem value="800">800 (Extra Bold)</MenuItem>
                      <MenuItem value="900">900 (Black)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Navigation Links Styling */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Navigation Links Styling</Typography>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle1">Link Color:</Typography>
                  <Button variant="outlined" onClick={(e) => handleColorPickerOpen('linkColor', e)} startIcon={<ColorLensIcon />} fullWidth sx={{ mt: 1 }}>{navbarData.linkColor}</Button>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle1">Hover Color:</Typography>
                  <Button variant="outlined" onClick={(e) => handleColorPickerOpen('linkHoverColor', e)} startIcon={<ColorLensIcon />} fullWidth sx={{ mt: 1 }}>{navbarData.linkHoverColor}</Button>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle1">Active Color:</Typography>
                  <Button variant="outlined" onClick={(e) => handleColorPickerOpen('activeLinkColor', e)} startIcon={<ColorLensIcon />} fullWidth sx={{ mt: 1 }}>{navbarData.activeLinkColor}</Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Font Size (px)"
                    type="number"
                    name="navLinkFontSize"
                    value={navbarData.navLinkFontSize || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    helperText="Pixel value"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Font Family</InputLabel>
                    <Select
                      name="navLinkFontFamily"
                      value={navbarData.navLinkFontFamily || 'Arial'}
                      onChange={handleInputChange}
                      label="Font Family"
                    >
                      {commonFontFamilies.map(font => (
                        <MenuItem key={font} value={font}>{font}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Font Weight</InputLabel>
                    <Select
                      name="navLinkFontWeight"
                      value={navbarData.navLinkFontWeight || '500'}
                      onChange={handleInputChange}
                      label="Font Weight"
                    >
                      <MenuItem value="100">100 (Thin)</MenuItem>
                      <MenuItem value="200">200 (Extra Light)</MenuItem>
                      <MenuItem value="300">300 (Light)</MenuItem>
                      <MenuItem value="400">400 (Normal)</MenuItem>
                      <MenuItem value="500">500 (Medium)</MenuItem>
                      <MenuItem value="600">600 (Semi Bold)</MenuItem>
                      <MenuItem value="700">700 (Bold)</MenuItem>
                      <MenuItem value="800">800 (Extra Bold)</MenuItem>
                      <MenuItem value="900">900 (Black)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Color Picker Popover */}
        <Popover
          open={Boolean(colorPickerAnchorEl)}
          anchorEl={colorPickerAnchorEl}
          onClose={handleColorPickerClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Paper elevation={8} sx={{ p: 1 }}>
            <ChromePicker
              color={activeColorPicker && navbarData[activeColorPicker] ? navbarData[activeColorPicker] : '#FFFFFF'}
              onChangeComplete={handleColorChange}
              disableAlpha
            />
            <Button onClick={handleColorPickerClose} variant="contained" size="small" fullWidth sx={{ mt: 1 }}>Close</Button>
          </Paper>
        </Popover>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="h6" gutterBottom>Navigation Links Management</Typography>
          <Typography variant="body1" gutterBottom>
            Currently, navigation links (e.g., Home, About, Services) are managed directly in the frontend code, specifically within the <code>Header.tsx</code> or a similar navigation component.
          </Typography>
          <Typography variant="body2">
            To make these links dynamically editable through this admin panel, significant updates would be required:
          </Typography>
          <ul>
            <li>
              <Typography variant="caption">The backend API (<code>navbarSection</code>) would need to be extended to store and serve an array of navigation link objects (e.g., <code>{`[{ text: 'Home', url: '/' }, ... ]`}</code>).</Typography>
            </li>
            <li>
              <Typography variant="caption">This admin editor would need new UI elements (e.g., a list editor for links with fields for text, URL, order, etc.).</Typography>
            </li>
            <li>
              <Typography variant="caption">The frontend navigation component would need to be refactored to fetch these links from the API instead of using a hardcoded list.</Typography>
            </li>
          </ul>
          <Typography variant="body2" sx={{mt:1}}>
            This is a common feature enhancement. If you'd like to implement this, please let me know!
          </Typography>
        </Alert>
      </TabPanel>

    </Paper>
  );
};

export default HeaderEditor;
